<template>
  <div class="transactions-log">
    <div class="flex flex-wrap">
      <div class="w-full title mb-8 md:title">{{ $t('system_fund.table_title') }}</div>

      <div class="inputs flex w-full flex-wrap md:flex-no-wrap md:justify-end md:mb-4">
        <div class="user-email-input-parent w-full mb-4 md:filter md:mb-0 md:mr-4">
          <input
            v-model="searchInput"
            id="system-fund-table-user-email-input"
            class="user-email-input"
            type="text"
            placeholder="Email or transaction ID"
          >
          <vs-icon class="user-email-icon" icon-pack="speakol" icon="speakol-search"></vs-icon>
        </div>

        <speakol-date-range-picker
          id="system-fund-table-date-range-input"
          v-on:date-change="handleDateChange"
          v-on:date-selected="handleDateSelect"
          class="w-full mb-6 md:mb-0 md:filter md:mr-4"
          :dateRange="filters.dateRange"
          placeholder="Filter by date"
          v-on:closed="onDateRangePickerClose"
        >
          <template v-if="!isValidDaterangeToExport" slot="actions-footer">
            <div class="datepicker-warning-container m-3 p-4">
              <vs-icon class="warning-icon mr-3" color="var(--warningColor)" size="16px" icon="speakol-warning" icon-pack="speakol"></vs-icon>
              {{ $t("system_fund.export_date_limit_msg") }}
            </div>
          </template>
        </speakol-date-range-picker>

        <speakol-select
            id="system-fund-table-payment-method-list"
            placeholder="Payment Method"
            v-model="filters.payment_method"
            :options="paymentMethodOptions"
            ref="payment-method"
            class="w-full mb-4 md:filter md:mb-0 md:mr-4"
            :reduce="method => method.value"
        ></speakol-select>

        <speakol-select
            id="system-fund-table-status-list"
            placeholder="Status"
            v-model="filters.status"
            :options="statusOptions"
            ref="status"
            class="w-full mb-4 md:filter md:mb-0 md:mr-4"
            :reduce="status => status.value"
        ></speakol-select>

        <vs-button
          id="system-fund-table-filter-button"
          class="w-full mb-10 md:btn md:mb-0 btn shadow-none"
          v-on:click="applyFilters"
        >
          {{ $t('general.filters_action_btn') }}
        </vs-button>

        <button id="all-filters-button-parent"
          class="shadow-none speakol-table-export-button border-transparent"
          @click="isExportPopupActive=true"
          :class="{
            'export-enabled': isValidDaterangeToExport,
            'export-empty-data-btn': isExportButtonDisabled,
            'bg-speakol-color cursor-pointer': isValidDaterangeToExport,
            'export-disabled flex': !isValidDaterangeToExport
          }"
          :disabled="isExportButtonDisabled || !isValidDaterangeToExport">
            <vx-tooltip class="tooltip" v-if="!isValidDaterangeToExport" :text="exportTooltipText">
              <vs-icon size="small" class="mr-2" icon="speakol-warning-2" icon-pack="speakol"></vs-icon>
            </vx-tooltip>
          {{ $t('general.export_btn') }}
        </button>
      </div>

    </div>

    <a-g-speakol-table
      :columns="columns"
      :options="options"
      :filters="filters"
      ref="transaction-log"
      @sortChanged="sortChanged"
      @dataChange="handleDataChange"
    >
    </a-g-speakol-table>

    <SpeakolExport :fileName="'SystemFunds'" :isActive="isExportPopupActive" exportType="system-funds"
      :params="exportParams" @hide="isExportPopupActive=false"/>

    <SpeakolNotificationToaster :show="toaster.show" :message="toaster.message" :icon="toaster.icon"
      :closeButtonText="toaster.closeButtonText" @close="toaster.show=false">

      <template v-slot:close>
        <vs-icon class="x-icon" color="white" icon="speakol-cancel" icon-pack="speakol"></vs-icon>
      </template>
    </SpeakolNotificationToaster>
  </div>
</template>

<script>

import {get} from 'lodash'
import AGSpeakolTable from '@/components/AGSpeakolTable'
import SystemFundDataSource from '@/datasources/SystemFundDataSource'
import DateColumn from './date-col'
import AccountColumn from './account-col'
import StatusColumn from './status-col'
import SpeakolExport from '@/components/SpeakolExport'
import SpeakolNotificationToaster from '@/components/SpeakolNotificationToaster'
import {formatToDateString, getDatesDiffInMonth} from '@/filters/Dates'

export default {
  components: {
    AGSpeakolTable,
    SystemFundDataSource,
    DateColumn,
    AccountColumn,
    StatusColumn,
    SpeakolExport,
    SpeakolNotificationToaster
  },

  filters: {
    formatToDateString,
    getDatesDiffInMonth
  },

  data () {
    const self = this
    return {
      isValidDaterangeToExport: false,
      exportTooltipText: self.$t('system_fund.export_date_limit_tooltip'),
      isExportButtonDisabled: false,
      isExportPopupActive: false,
      exportSortModel: {
        colId: 'id',
        sort: 'desc'
      },
      toaster: {
        show: false,
        message: '',
        icon: require('@/assets/images/success.svg'),
        closeButtonText: 'close'
      },
      options: {
        dataSource: SystemFundDataSource,
        rowHeight: 56
      },
      columns: [
        {
          headerName: 'ID',
          field: 'transactions.id',
          sortable: true,
          minWidth: 95,
          valueGetter (params) {
            return get(params, 'data.id')
          }
        },
        {
          headerName: 'Account',
          field: 'source.name',
          sortable: true,
          cellRendererFramework: 'AccountColumn',
          minWidth: 266
        },
        {
          headerName: 'Status',
          field: 'transactions.confirmed_at',
          sortable: true,
          cellRendererFramework: 'StatusColumn',
          minWidth: 130
        },
        {
          headerName: 'Descripiton',
          field: 'transactions.type',
          sortable: true,
          minWidth: 160,
          valueGetter (params) {
            const type =  get(params, 'data.type')

            if (type === self.$constants.TRANSACTION_TYPES.VAT_CORRECTION) {
              return self.$t('Vat Correction')
            } else if (type === self.$constants.TRANSACTION_TYPES.FUND) {
              return self.$t('Fund')
            }
            return self.$t('Refund')
          }
        },
        {
          headerName: 'Date',
          field: 'transactions.created_at',
          sortable: true,
          minWidth: 160,
          cellRendererFramework: 'DateColumn'
        },
        {
          headerName: 'Added By',
          field: 'added_by_data.name',
          minWidth: 210,
          sortable: true,
          valueGetter (params) {
            return get(params, 'data.added_by_name')
          }
        },
        {
          headerName: 'Payment Method',
          field: 'payment_types.name',
          minWidth: 200,
          sortable: true,
          valueGetter (params) {
            return get(params, 'data.payment_type_name', '')
          }
        },
        {
          headerName: 'Amount',
          field: 'balance',
          minWidth: 155,
          sign: '$',
          sortable: true,
          resizable: false,
          valueGetter (params) {
            return self.$options.filters.addComma(get(params, 'data.balance', 0), '$')
          }

        }
      ],

      // Ag filter params
      filters: {
        dateRange: {
          startDate: new Date('January 1, 2016'),
          endDate: new Date()
        },
        payment_method: null,
        status: null,
        email: null,
        transaction_id: null
      },
      searchInput: null,

      paymentMethodOptions: [
        { text: 'Select All', value: null},
        { text: 'Cash', value: 'Cash' },
        { text: 'Bank', value: 'Bank' },
        { text: 'Stripe', value: 'Stripe' },
        { text: 'Paypal', value: 'Paypal' },
        { text: 'Paytabs', value: 'Paytabs' },
        { text: 'Checkout', value: 'Checkout' },
        { text: 'Bonus', value: 'Bonus' },
        { text: 'Compensation', value: 'Compensation' },
        { text: 'Credit', value: 'Credit' }
      ],
      statusOptions: [
        { text: 'Select All', value: null},
        { text: 'Confirmed', value: 'confirmed' },
        { text: 'Pending', value: 'pending' }
      ]
    }
  },

  computed: {
    exportParams () {
      return {
        ...this.filters,
        sort: [this.exportSortModel]
      }
    }
  },

  watch:{
    searchInput (newVal) {
      this.filters['email'] = this.filters['transaction_id'] = null
      this.filters[this.isEmail(newVal) ? 'email' : 'transaction_id'] = newVal
    }
  },

  methods: {
    handleDateSelect (date) {
      this.isValidDaterangeToExport = this.checkCanExportInDateRange(date.endDate, date.startDate)
    },

    checkCanExportInDateRange (endDate, startDate) {
      const datesDiffInMonths = getDatesDiffInMonth(endDate, startDate)

      if (datesDiffInMonths > 3) {
        return false
      }

      return true
    },

    handleDateChange (date) {
      this.filters.dateRange.startDate = formatToDateString(date.startDate)
      this.filters.dateRange.endDate = formatToDateString(date.endDate)
    },

    handleDataChange ({totalItems}) {
      this.isExportButtonDisabled = totalItems === 0
    },

    onDateRangePickerClose () {
      this.handleDateSelect(this.filters.dateRange)
    },

    applyFilters () {
      this.$emit('filter-change', this.filters)

      this.$refs['transaction-log'].applyFilters()
    },

    isEmail (str) {
      return /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(.\w{2,3})+$/.test(str)
    },

    sortChanged (sortModel) {
      const sortColumn = sortModel[0] || {}

      this.exportSortModel = {
        colId: 'id',
        sort: 'desc'
      }

      if (sortColumn.colId && sortColumn.sort) {
        this.exportSortModel = {
          colId: sortColumn.colId,
          sort: sortColumn.sort
        }
      }
    }
  }
}

</script>


<style lang="scss" scopped>
$disabled-color: var(--gray);
$warning-color: var(--yellow);

.tooltip {
  font-size: 14px;
  line-height: 21px;
  letter-spacing: 0.1px;
}

.datepicker-warning-container {
  border: 1px solid $warning-color;
  box-sizing: border-box;
  border-radius: 4px;
  line-height: 1.5;
  background-color: var(--yellowBackground);
}

.export-empty-data-btn {
  opacity: 0.5;
  cursor: default;
}

.speakol-table-export-button {
  border-radius: 3px;
  padding: 7px 20px;
  width: 135px;
  cursor: pointer;
  height: 40px;
  font-size: 14px;
  margin-left: 10px;
}

.export-enabled {
  border: 1px solid var(--speakolColor) !important;
  background-color: var(--white) !important;
  color: var(--speakolColor);
}

.export-disabled {
  line-height: 1.5;
  background-color: var(--white) !important;
  color: $disabled-color !important;
  border: 1px solid $disabled-color !important;
}

@media (max-width: 768px) {
  .speakol-table-export-button {
    width: 100%;
    margin-left: 0;
    margin-bottom: 20px;
    padding-left: 36%;
  }
}

.transactions-log {
  .inputs {
    height: auto;
  }

  .btn {
    height: 40px;
    border-radius: 3px;
  }

  .title {
    font-style: normal;
    font-weight: 500;
    font-size: 18px;
    line-height: 27px;
    letter-spacing: 0.1px;
    color: #2c2c2c;
  }

  @media (min-width: 768px) {
    .md\:inputs {
      height: 40px;
    }

    .md\:btn {
      width: 135px !important;
    }

    .md\:title {
      font-style: normal;
      font-weight: 500;
      font-size: 24px !important;
      line-height: 36px;
      color: #2c2c2c;
    }

    .md\:filter {
      width: 260px !important;
    }
  }

  .daterangepicker {
    position: absolute;
    z-index: 9999999;
  }

  .user-email-input-parent {
    position: relative;

    .user-email-input {
      width: 100%;
      height: 40px;
      text-indent: 35px;
      border-radius: 4px;
      border: 1px solid rgba(60, 60, 60, 0.26);
    }

    .user-email-icon {
      position: absolute;
      z-index: 9;
      left: 0;
      color: #bdbdbd;
      left: 10px;
      top: 8px;
    }

    .user-email-input::placeholder {
      font-size: 14px;
      color: #bdbdbd;
    }
  }
}
</style>

